import type { BasicPresenceFragment } from '@drycut/graphql';

import { DateTime } from 'luxon';

type DaySlot = {
  end: DateTime;
  start: DateTime;
};

interface DaySchedule extends DaySlot {
  closed: boolean;
}

// Checks if there is a non-recurring presence that overrides the provided presence. This assumes
// that an employee can have only one presence per weekday
function getOverride(
  presence: BasicPresenceFragment,
  presences: BasicPresenceFragment[],
): BasicPresenceFragment | undefined {
  return presences.find(
    (p) =>
      p.id !== presence.id &&
      p.employeeId === presence.employeeId &&
      p.recurring === false &&
      DateTime.fromISO(p.startTime).weekday === DateTime.fromISO(presence.startTime).weekday,
  );
}

export function buildPresenceSlots(date: DateTime, presences: BasicPresenceFragment[]): DaySlot[] {
  // Find presences for the provided date. The only reliable way is to compare the day of the week
  // and the date itself as presences can be recurring
  const weekDayPresences = presences.filter(
    (presence) => DateTime.fromISO(presence.startTime).weekday === date.weekday,
  );

  // Get the presence overrides or the original presence if no override exists. Then filter out the
  // invalid presences (those that have been overridden)
  const validPresences = weekDayPresences
    .map((presence) => getOverride(presence, presences) || presence)
    .filter((presence) => !presence.overrideReason);

  // If there are no valid presences for the day, return a closed schedule
  if (validPresences.length === 0) {
    return [];
  }

  // Return an array of DaySlot objects for each valid presence
  return validPresences.map((presence) => {
    const start = DateTime.fromISO(presence.startTime);
    const end = DateTime.fromISO(presence.endTime);
    return {
      start: date.set({ hour: start.hour, minute: start.minute }),
      end: date.set({ hour: end.hour, minute: end.minute }),
    };
  });
}

export function extendSlotsToSingleSchedule(date: DateTime, slots: DaySlot[]): DaySchedule {
  if (slots.length === 0) {
    return {
      start: date,
      end: date,
      closed: true,
    };
  }

  // Return the schedule based on the earliest start time and latest end time of the provided slots
  const start = slots.reduce((earliest, slot) => {
    return slot.start < earliest ? slot.start : earliest;
  }, slots[0].start);
  const end = slots.reduce((latest, slot) => {
    return slot.end > latest ? slot.end : latest;
  }, slots[0].end);

  return {
    start,
    end,
    closed: false,
  };
}
